import { Label } from './Label.js';
import { Logger } from './Logger.js';
import { Message } from './Message.js';
import { Rgb } from './Rgb.js';
import { ShortPlacementId } from './ShortPlacementId.js';
import type { Tuple } from '../types/index.js';

export class LoggerFactory<
  TLabels extends ReadonlyArray<string> = Tuple.Empty
> {
  static #baseInstance?: LoggerFactory;

  static get baseInstance() {
    return (this.#baseInstance ??= new this());
  }

  readonly #labels: Label.Mapper<TLabels>;

  protected constructor(labels?: Label.Mapper<TLabels>) {
    this.#labels = labels ?? ([] as Label.Mapper<TLabels>);
  }

  message<const TData extends Tuple.Any>(
    ...args: Message.Args<TData>
  ): Logger<TData>;

  message(
    ...args: Message.TemplateFunctionArgs
  ): <const TData extends Tuple.Any>(...data: TData) => Logger<TData>;

  message<const TData extends Tuple.Any>(
    ...args: Message.Args<TData> | Message.TemplateFunctionArgs
  ):
    | Logger<TData>
    | (<const TData extends Tuple.Any>(...data: TData) => Logger<TData>) {
    if (typeof args[0] === 'string') {
      const [message, ...data] = args as Message.Args<TData>;
      return new Logger(message, Message.Labels.from(this.#labels), data);
    }

    return <const TData extends Tuple.Any>(...data: TData): Logger<TData> => {
      const message = args as Message.TemplateFunctionArgs;
      return new Logger(message, Message.Labels.from(this.#labels), data);
    };
  }

  withLabel<TLabel extends string>(
    text: TLabel,
    textColor = Rgb.black,
    backgroundColor = Rgb.white
  ) {
    return new LoggerFactory<readonly [...TLabels, TLabel]>([
      ...this.#labels,
      { text, textColor, backgroundColor }
    ] as const);
  }

  withPlacementLabel<TPlacementId extends string>(
    placementId: TPlacementId,
    textColor = Rgb.pinkDark,
    backgroundColor = Rgb.pink
  ) {
    const text = ShortPlacementId.from(placementId);

    return new LoggerFactory<readonly [typeof text, ...TLabels]>([
      { text, textColor, backgroundColor },
      ...this.#labels
    ]);
  }

  withSublabel<
    const TLabel extends TLabels[number],
    const TSublabel extends string
  >(
    labelText: TLabel,
    sublabelText: TSublabel
  ): LoggerFactory<Label.Sublabel.Mapper<TLabels, TLabel, TSublabel>> {
    return new LoggerFactory(
      Array.from(
        Label.Sublabel.produce(this.#labels, labelText, sublabelText)
      ) as Label.Mapper<Label.Sublabel.Mapper<TLabels, TLabel, TSublabel>>
    );
  }
}

export namespace LoggerFactory {
  export type Any = LoggerFactory<ReadonlyArray<string>>;

  export class Void<
    TLabels extends ReadonlyArray<string> = Tuple.Empty
  > extends LoggerFactory<TLabels> {
    static #baseInstance?: Void;

    static override get baseInstance() {
      return (this.#baseInstance ??= new this());
    }

    private constructor() {
      super();
    }

    override message(arg: string | TemplateStringsArray) {
      return (
        typeof arg === 'string'
          ? Logger.Void.instance
          : () => Logger.Void.instance
      ) as never;
    }

    override withLabel() {
      return this as never;
    }

    override withPlacementLabel() {
      return this as never;
    }

    override withSublabel() {
      return this as never;
    }
  }
}
